import { ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy } from '@angular/core';
import { MessageCenterHeaderComponent } from './message-center-header/message-center-header.component';
import { MessageCenterThreadListComponent } from './message-center-thread-list/message-center-thread-list.component';
import { MessageCenterChatInterfaceComponent } from './message-center-chat-interface/message-center-chat-interface.component';
import { MessageCenterCategorySelectorComponent } from './message-center-category-selector/message-center-category-selector.component';
import { MessageCenterService } from '@k12/messaging';
import { MsalService } from '@azure/msal-angular';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MessageCenterChatFormComponent } from './message-center-chat-form/message-center-chat-form.component';
import { CreateTopicPayload, Thread } from './message-center.model';
import { MessageCenterThreadFilterComponent } from './message-center-thread-filter/message-center-thread-filter.component';
import { MessageCenterChatHeaderComponent } from './message-center-chat-header/message-center-chat-header.component';
import { isSameAccountToken, normalizeAccountToken, SystemService, AppInsightsService } from '@k12/app-services';
import { Subject, takeUntil } from 'rxjs';
import { ModalDialogComponent } from '@k12/ui-primitives';
import { ModalType } from '@k12/models';
import { MatDialog } from '@angular/material/dialog';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-message-center',
  imports: [
    MessageCenterHeaderComponent,
    MessageCenterThreadListComponent,
    MessageCenterCategorySelectorComponent,
    MessageCenterChatInterfaceComponent,
    MatButtonModule,
    MatIconModule,
    MessageCenterChatFormComponent,
    MessageCenterThreadFilterComponent,
    MessageCenterChatHeaderComponent
  ],
  templateUrl: './message-center.component.html',
  styleUrl: './message-center.component.scss'
})
export class MessageCenterComponent implements OnInit, OnDestroy {
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly messageService: MessageCenterService = inject(MessageCenterService);
  private readonly msalService: MsalService = inject(MsalService);
  private readonly systemService: SystemService = inject(SystemService);
  private readonly destroyed: Subject<void> = new Subject<void>();
  private readonly promptDialog = inject(MatDialog);
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly accountToken: string = '';
  public readonly accountId?: string = '';
  public createTopicPayload: CreateTopicPayload | null = null;
  public isLoading: boolean = true;
  public threads: Thread[] = [];
  public filteredThreads: Thread[] = [];
  public selectedThread: Thread | null = null;
  public messageState: 'display' | 'create' = 'create';
  public messageInProgress: boolean = false;

  @Input() programPartnerTypeId: number = 0;
  @ViewChild('chat', { read: ElementRef }) chatElement!: ElementRef<HTMLDivElement>;
  @ViewChild('chatForm') chatForm!: MessageCenterChatFormComponent;

  constructor() {
    this.accountId = this.msalService.instance.getActiveAccount()?.localAccountId;
    if (this.accountId) {
      this.accountToken = normalizeAccountToken(this.accountId);
    } else {
      // handle the errors
    }
  }

  public ngOnInit(): void {
    this.loadThreads();

    // SignalR event relay (internal Subject); cannot error by construction, so no error handler is needed.
    this.messageService.newMessage.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.loadThreads(false);
    });

    this.messageService.newUnassignedTopic.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.loadThreads(false);
    });

    this.messageService.messageReply.pipe(takeUntil(this.destroyed)).subscribe((msg) => {
      const out = JSON.parse(msg);
      const accountMessageTopicId = out.body.accountMessageTopicId;
      out.body.recipientToken = out.recipient;

      if (this.selectedThread?.id == accountMessageTopicId) {
        out.body.recipientToken = out.recipient;
        this.selectedThread?.messages.push(out.body);
        this.selectedThread!.unReadCount = this.selectedThread!.messages.filter(
          (x) => isSameAccountToken(x.recipientToken, this.accountToken) && !x.dateRead
        ).length;
        this.markAsRead(true);
      } else {
        out.body.recipientToken = out.recipient;
        const thread = this.threads.find((x) => x.id == accountMessageTopicId);
        if (thread) {
          thread.unReadCount++;
          thread.messages.push(out.body);
          this.loadThreads(false);
        } else {
          this.loadThreads(false);
        }
      }
      this.cdr.detectChanges();
      this.scrollToChatBottom();
    });

    // SignalR event relay (internal Subject); cannot error by construction, so no error handler is needed.
    this.messageService.unReadUpdate.pipe(takeUntil(this.destroyed)).subscribe((msg) => {
      const out = JSON.parse(msg);
      if (out.body.length == 0) {
        return;
      }
      this.cdr.detectChanges();
    });

    this.messageService.topicArchived.pipe(takeUntil(this.destroyed)).subscribe((msg) => {
      const out = JSON.parse(msg);
      if (out.body.accountMessageTopicId == this.selectedThread?.id) {
        this.selectedThread = null;
      }
      this.loadThreads(false);
      this.cdr.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  public archiveThread() {
    if (this.selectedThread === null) return;
    const payload = {
      accountMessageTopicId: this.selectedThread.id
    };
    this.messageService.ArchiveAccountMessageTopics(payload).subscribe({
      next: () => {
        this.selectedThread!.dateArchived = new Date();
        this.selectedThread = null;
        this.loadThreads(false);
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  public handleNewThread(payload: CreateTopicPayload) {
    this.createTopicPayload = payload;
    this.selectedThread = {
      parentCategoryName: this.createTopicPayload.parentCategoryName,
      childCategoryName: this.createTopicPayload.childCategoryName,
      dateArchived: null,
      id: 'new',
      messages: [],
      assignedToId: '',
      participantId: this.accountId,
      dateCreated: new Date(),
      lastSent: 0,
      unReadCount: 0,
      description: ''
    } as Thread;
    this.messageState = 'display';
  }

  public handleFilteredThreadsChanged(threads: Thread[]) {
    queueMicrotask(() => {
      this.filteredThreads = threads;
    });
  }

  public handleNewMessageSelected(): void {
    const startNewMessage = () => {
      this.messageState = 'create';
      this.selectedThread = null;
      this.messageInProgress = false;
    };

    // if there is a message in progress ask for confirmation before changing the thread
    if (!this.messageInProgress) {
      // No message, allow navigation without confirmation
      startNewMessage();
      return;
    }

    const promptDialogRef = this.promptDialog.open(ModalDialogComponent, {
      backdropClass: 'custom-dialog-backdrop',
      data: {
        closeButtonText: 'No',
        confirmButtonText: 'Yes',
        modalType: ModalType.ContinueCancelModal,
        title: `You have an unsent message. If you leave you will lose this message, continue?`
      },
      disableClose: true,
      panelClass: 'custom-dialog'
    });

    promptDialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        startNewMessage();
      }
    });
  }

  public handleNewThreadCreated(thread: Thread) {
    this.threads.push(thread);
    this.selectedThread = thread;
    this.messageState = 'display';
    this.createTopicPayload = null;
    // delay just to make sure the new thread is created by the api
    setTimeout(() => this.loadThreads(false), 100);
  }

  public handleMessageChanges(message: string) {
    if (message.length === 0) {
      this.messageInProgress = false;
      return;
    }
    this.messageInProgress = true;
  }

  public handleMessageSent(): void {
    if (this.selectedThread === null) return;
    this.messageInProgress = false;
    this.loadThreads(false);
    this.scrollToChatBottom();
  }

  public handleThreadChanged(thread: Thread) {
    this.selectedThread = thread;
    this.messageState = 'display';
    this.markAsRead();
    this.scrollToChatBottom();
    this.loadThreads(false);
  }

  private loadThreads(displayWaiting: boolean = true) {
    if (displayWaiting) {
      this.isLoading = true;
    }
    this.messageService.GetAllAccountMessagesAndTopics().subscribe({
      next: (res) => {
        res.forEach((item) => {
          // get the last message sent from messages.messages array
          // and convert to numerical representation
          // then sort
          let out = 0;
          if (item.messages[item.messages.length - 1]?.dateSent) {
            const tmp = new Date(item.messages[item.messages.length - 1].dateSent);
            out = tmp.getTime();
          }
          item.lastSent = out;
          item.unReadCount = item.messages.filter((x) => isSameAccountToken(x.recipientToken, this.accountToken) && !x.dateRead).length;
        });
        res.sort((a, b) => b.lastSent - a.lastSent);
        this.threads = res; //.filter(x => !x.dateArchived);
        this.syncUnreadMessageCount();
        // reset the selected Thread based on the id
        this.selectedThread = this.threads.find((x) => x.id === this.selectedThread?.id) ?? null;
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.isLoading = false;
        this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  public markAsRead(overrideUnreadCount: boolean = false): void {
    if (this.selectedThread === null) return;
    if (this.selectedThread.unReadCount === 0 && !overrideUnreadCount) return;

    const payload = {
      accountMessageTopicId: this.selectedThread.id,
      isRead: true
    };
    this.messageService.ToggleAccountMessageTopicAsRead(payload).subscribe({
      next: () => {
        if (this.selectedThread) {
          this.selectedThread.unReadCount = 0;
        }
        this.loadThreads(false);
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  public markAsUnread() {
    if (this.selectedThread === null) return;
    if (this.selectedThread.unReadCount > 0) return;

    const payload = {
      accountMessageTopicId: this.selectedThread.id,
      isRead: false
    };

    this.messageService.ToggleAccountMessageTopicAsRead(payload).subscribe({
      next: () => {
        this.selectedThread = null;
        this.loadThreads(false);
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  public scrollToChatBottom(): void {
    setTimeout(() => {
      if (!this.chatElement) return;
      const el = this.chatElement.nativeElement;
      el.scrollTop = el.scrollHeight;
    }, 10);
  }

  private syncUnreadMessageCount(): void {
    this.systemService.unreadMessageCount = this.unReadMessageCount;
  }

  public get unReadMessageCount() {
    return this.threads.filter((x) => x.unReadCount > 0).length ?? 0;
  }
}
